import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';
import { SimulasiProduksiReport } from '@/components/print/SimulasiProduksiReport';
import SuratJalanPrint from '@/components/production/SuratJalanPrint';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { useClientPOs } from '@/hooks/useClientPOs';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog';
import { 
  FlaskConical, 
  FileText, 
  Coins, 
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Package,
  Droplets,
  Users,
  Calendar,
  X,
  Upload,
  ArrowLeft,
  Bot,
  Download,
  AlertTriangle,
  Activity,
  Check,
  Truck
} from 'lucide-react';

const mockPOCards = [];

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const items = [];
  const delta = 1;

  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      items.push(i);
    }
  }

  const result = [];
  let prev = 0;

  for (const page of items) {
    if (prev) {
      if (page - prev === 2) {
        result.push(prev + 1);
      } else if (page - prev > 2) {
        result.push('...');
      }
    }
    result.push(page);
    prev = page;
  }

  return result;
};

// Simple line chart component for the top stats
const Sparkline = ({ color = "#8C6208" }) => (
  <div className="w-full h-8 mt-2 opacity-60">
    <svg viewBox="0 0 100 25" preserveAspectRatio="none" className="w-full h-full">
      <path 
        d="M0,25 C10,15 20,20 30,10 C40,0 50,15 60,10 C70,5 80,20 90,10 L100,0" 
        fill="none" 
        stroke={color} 
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  </div>
);

const getTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Baru saja';
  if (minutes < 60) return `${minutes} mnt lalu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} jam lalu`;
  const days = Math.floor(hours / 24);
  return `${days} hari lalu`;
};

export default function SimulasiBiayaProduksi() {
  const { clientPOs, isLoading } = useClientPOs();
  
  const { data: productionOrders = [] } = useQuery({
    queryKey: ['production-orders-simulasi'],
    queryFn: () => base44.entities.ProductionOrder.list()
  });

  const { data: allTasks = [] } = useQuery({
    queryKey: ['production-tasks-simulasi'],
    queryFn: () => base44.entities.ProductionTask.list('-created_date')
  });

  const { data: companySettings } = useQuery({
    queryKey: ['company-settings'],
    queryFn: async () => {
      const res = await base44.entities.CompanySettings.list();
      return res?.[0] || {};
    }
  });

  const activePOs = useMemo(() => clientPOs.filter(po => {
    const st = po.statusProduksi || po.status || '';
    return st.toUpperCase() !== 'BATAL';
  }).map(po => {
    // 1. Dapatkan ProductionOrder & Task Rejects
    const matchedPO = productionOrders.find(o => String(o.order_number) === String(po.id));
    let totalRejects = 0;
    let rejectsByVariant = {};
    let sjLink = null;
    
    if (matchedPO) {
      if (matchedPO.shipped_at || matchedPO.shipping_date) {
         sjLink = `/office/production/sj/${matchedPO.id}`;
      }
      const poTasks = allTasks.filter(t => t.po_id === matchedPO.id);
      poTasks.forEach(task => {
        if (!task.member_progress) return;
        Object.values(task.member_progress).forEach(prog => {
          if (prog.status === 'completed' && prog.rejected_items?.length) {
            prog.rejected_items.forEach(rj => {
              rj.items.forEach(item => {
                const qty = Number(item.qty) || 0;
                totalRejects += qty;
                const vName = rj.variantName || 'Produk Utama';
                rejectsByVariant[vName] = (rejectsByVariant[vName] || 0) + qty;
              });
            });
          }
        });
      });
    }

    let _brand = po.brand || po.brand_name || po.client || '';
    let _pic = po.pic || po.pic_id || '';
    const custName = po.customerName || '';
    if (!_brand && custName) {
      _brand = custName.includes(' / ') ? custName.split(' / ')[0].trim() : custName;
    }
    if (!_pic && custName && custName.includes(' / ')) {
      _pic = custName.split(' / ')[1].trim();
    }
    
    let _qty = po.qty;
    if (typeof _qty !== 'number') {
      _qty = 0;
      if (po.products && po.products.length) {
        _qty = po.products.reduce((s, p) => s + (p.qty || 0), 0);
      } else if (po.items && po.items.length) {
        _qty = po.items.reduce((s, i) => s + (i.qty || 0), 0);
      }
    }
    
    let _variants = [];
    const prods = po.products || po.items || [];
    prods.forEach(p => {
      const pFallbackMl = parseFloat(p.nama_variant?.match(/\((\d+)ml\)/)?.[1] || 0);
      const pVol = p.custom_ukuran_aktual_ml || p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || pFallbackMl;
      
      if (p.is_discovery_set) {
        (p.items || []).forEach(it => {
          const fallbackMl = parseFloat(it.nama_variant?.match(/\((\d+)ml\)/)?.[1] || 0);
          const volVariant = it.custom_ukuran_aktual_ml || it.botol_ukuran_aktual || it.botol_ukuran_label || it.botol_ukuran_client || it.ukuran_botol_ml || fallbackMl || pVol;
          
          let rawNames = (it.nama_variant || it.nama_product || it.nama_produk || it.produk || it.nama || it.name || p.variant || p.nama_variant || p.nama_product || p.nama_produk || p.produk || p.nama || p.name || 'Variant').split(',');
          rawNames.forEach(rn => {
             let name = rn.replace(/\s*\(?\s*\d+\s*pcs\s*\)?\s*/gi, '').trim();
             if (volVariant > 0 && !name.toLowerCase().includes('ml')) name += ` (${volVariant} ml)`;
             if (name) _variants.push(name);
          });
        });
      } else {
        let rawNames = (p.variant || p.nama_variant || p.nama_product || p.nama_produk || p.produk || p.nama || p.name || 'Variant').split(',');
        rawNames.forEach(rn => {
           let name = rn.replace(/\s*\(?\s*\d+\s*pcs\s*\)?\s*/gi, '').trim();
           if (pVol > 0 && !name.toLowerCase().includes('ml')) name += ` (${pVol} ml)`;
           if (name) _variants.push(name);
        });
      }
    });
    
    // Fallback if empty and replace pcs in fallback too
    if (_variants.length === 0) {
       let fallbackStr = po.varianLabel || po.varian || po.variant || 'Tanpa Varian';
       _variants = fallbackStr.split(',').map(rn => rn.replace(/\s*\(?\s*\d+\s*pcs\s*\)?\s*/gi, '').trim()).filter(Boolean);
       if (_variants.length === 0) _variants = ['Tanpa Varian'];
    }
    _variants = [...new Set(_variants)];

    const _dateStr = po.rawRecord?.updated_at || po.updated_at || po.date || po.tanggal || po.created_at || po.created_date || po.tanggal_invoice;
    const _computedDate = _dateStr ? new Date(_dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

    return { 
      ...po, 
      _computedBrand: _brand || 'Unknown Brand', 
      _computedPic: _pic || 'Unknown PIC', 
      _computedQty: _qty || 0, 
      _computedVariants: _variants, 
      _computedDate,
      _totalRejects: totalRejects,
      _rejectsByVariant: rejectsByVariant,
      _sjLink: sjLink,
      _productionOrder: matchedPO
    };
  }).filter(po => {
    if (!po._computedVariants || po._computedVariants.length === 0) return false;
    if (po._computedVariants.length === 1 && po._computedVariants[0].toLowerCase() === 'tanpa varian') return false;
    return true;
  }), [clientPOs, productionOrders, allTasks]);

  // Modal & Simulation State
  const [simulatedPOIds, setSimulatedPOIds] = useState([]);
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  const [suratJalanToPrint, setSuratJalanToPrint] = useState(null);

  useEffect(() => {
    if (!hasAutoLoaded && activePOs.length > 0) {
      const initialIds = activePOs
        .filter(po => {
          const st = po.statusProduksi || po.status || '';
          const isFinished = ['PESANAN SELESAI', 'SELESAI', 'DIKIRIM'].includes(st.toUpperCase());
          if (!isFinished) return false;
          
          const items = po.items || po.products || [];
          const isOnlyBpomHakiItems = items.length > 0 && items.every(item => {
            const n = (item.nama || item.nama_product || item.name || '').toLowerCase();
            return n.includes('bpom') || n.includes('haki');
          });
          
          const vLabel = (po.varianLabel || po.varian || po.variant || '').toLowerCase();
          const isOnlyBpomHakiVariant = (vLabel.includes('bpom') || vLabel.includes('haki')) && !vLabel.includes(' - ') && !vLabel.includes(',');

          if (isOnlyBpomHakiItems || (items.length === 0 && isOnlyBpomHakiVariant)) {
            return false;
          }

          return true;
        })
        .map(po => po.id);

      if (initialIds.length > 0) {
        setSimulatedPOIds(initialIds);
        setHasAutoLoaded(true);
      }
    }
  }, [activePOs, hasAutoLoaded]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPic, setModalPic] = useState('');
  const [modalBrand, setModalBrand] = useState('');
  const [modalPOId, setModalPOId] = useState('');
  const [modalPOSearch, setModalPOSearch] = useState('');

  const modalUniquePICs = useMemo(() => Array.from(new Set(activePOs.map(po => po._computedPic).filter(Boolean))).sort(), [activePOs]);
  const modalUniqueBrands = useMemo(() => {
    if (!modalPic) return [];
    return Array.from(new Set(activePOs.filter(po => po._computedPic === modalPic).map(po => po._computedBrand).filter(Boolean))).sort();
  }, [activePOs, modalPic]);
  const modalAvailablePOs = useMemo(() => {
    if (!modalPic || !modalBrand) return [];
    let pos = activePOs.filter(po => po._computedPic === modalPic && po._computedBrand === modalBrand);
    if (modalPOSearch) {
      const q = modalPOSearch.toLowerCase();
      pos = pos.filter(po => po?.id?.toLowerCase().includes(q) || (po?.brand && po.brand.toLowerCase().includes(q)) || (po?.varian && po.varian.toLowerCase().includes(q)));
    }
    return pos;
  }, [activePOs, modalPic, modalBrand, modalPOSearch]);

  const [searchQuery, setSearchQuery] = useState('');
  const [cardPage, setCardPage] = useState(1);
  const [selectedPOId, setSelectedPOId] = useState(null);
  const [detailFilter, setDetailFilter] = useState('Semua');
  const [activeMainTab, setActiveMainTab] = useState('cards');
  const [laporanFilter, setLaporanFilter] = useState('Semua');
  const [laporanPage, setLaporanPage] = useState(1);
  const [cardLimit, setCardLimit] = useState(6);
  const [laporanItemsPerPage, setLaporanItemsPerPage] = useState(10);

  // Filter state untuk Daftar Transaksi Produksi
  const [laporanSearch, setLaporanSearch] = useState('');
  const [laporanCatSelect, setLaporanCatSelect] = useState('Semua Kategori');
  const [laporanDateFrom, setLaporanDateFrom] = useState('');
  const [laporanDateTo, setLaporanDateTo] = useState('');

  // Filter state untuk Pencatatan PO (Cards)
  const [poTimeFilter, setPoTimeFilter] = useState('Semua Waktu');
  const [poCustomStart, setPoCustomStart] = useState('');
  const [poCustomEnd, setPoCustomEnd] = useState('');
  const [poPicFilter, setPoPicFilter] = useState('Semua PIC');
  const [poBrandFilter, setPoBrandFilter] = useState('Semua Brand');
  const [poBrandSearch, setPoBrandSearch] = useState('');

  const uniqueBrands = useMemo(() => Array.from(new Set(activePOs.map(po => po._computedBrand).filter(Boolean))).sort(), [activePOs]);
  const uniquePICs = useMemo(() => Array.from(new Set(activePOs.map(po => po._computedPic).filter(Boolean))).sort(), [activePOs]);

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'Laporan_Simulasi_Biaya_Produksi',
    pageStyle: `
      @page { size: A4 landscape !important; margin: 10mm !important; }
      @media print {
        body { margin: 0; }
        .page-break-inside-avoid { page-break-inside: avoid !important; break-inside: avoid !important; }
      }
    `
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', { 
      style: 'currency', 
      currency: 'IDR', 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Process data for dashboard
  const dashboardData = useMemo(() => {
    let totalBiayaProduksi = 0;
    
    const simulatedPOs = activePOs.filter(po => simulatedPOIds.includes(po.id));
    const processedPOs = simulatedPOs.map(po => {
      const products = po.products || po.items || [];
      const aggItems = {};

      let costPackaging = 0;
      let costBahanCair = 0;
      let costItemTambahan = 0;

      const addAggItem = (name, cat, subCat, qty, unit, price) => {
         const key = `${cat}_${subCat}_${name}_${price}`;
         if (!aggItems[key]) {
            aggItems[key] = { name, category: cat, subCategory: subCat, qty: 0, unit, price, isClientProvided: false };
         }
         aggItems[key].qty += qty;
      };

      products.forEach(p => {
         if (p.is_discovery_set) {
            (p.items || []).forEach(it => {
               let variantName = it.nama_variant || it.variant || it.nama_produk || it.produk || it.nama || it.name || 'Produk Utama';
               variantName = variantName.replace(/\s*\(?\s*\d+\s*pcs\s*\)?\s*/gi, '').trim();
               const fallbackMl = parseFloat(it.nama_variant?.match(/\((\d+)ml\)/)?.[1] || 0);
               const volVariant = it.custom_ukuran_aktual_ml || it.botol_ukuran_aktual || it.botol_ukuran_label || it.botol_ukuran_client || it.ukuran_botol_ml || fallbackMl;
               if (volVariant > 0 && !variantName.toLowerCase().includes('ml')) variantName += ` (${volVariant} ml)`;

               const vReject = (po._rejectsByVariant || {})[variantName] || 0;
               const originalQty = it.qty_variant || p.qty || 1;
               const qty = Math.max(0, originalQty - vReject);

               if (qty <= 0) return; // Skip if all rejected

               // Packaging
               if (it.is_fullset || it.hpp_kemasan !== undefined) {
                  const pPrice = it.fullset_harga_beli || it.hpp_kemasan || 0;
                  if (pPrice > 0) {
                     addAggItem(it.fullset_nama || it.nama_kemasan || 'Kemasan Fullset', 'Packaging', 'Fullset', qty, 'pcs', pPrice);
                     costPackaging += (pPrice * qty);
                  }
               } else {
                  if (it.botol_harga_beli > 0) {
                     addAggItem(it.botol_nama || it.nama_botol || 'Botol', 'Packaging', 'Botol', qty, 'pcs', it.botol_harga_beli);
                     costPackaging += (it.botol_harga_beli * qty);
                  }
                  if (it.tutup_harga_beli > 0) {
                     addAggItem(it.tutup_nama || it.nama_tutup || 'Tutup', 'Packaging', 'Tutup', qty, 'pcs', it.tutup_harga_beli);
                     costPackaging += (it.tutup_harga_beli * qty);
                  }
                  if (it.spray_harga_beli > 0) {
                     addAggItem(it.spray_nama || it.nama_spray || 'Spray', 'Packaging', 'Spray', qty, 'pcs', it.spray_harga_beli);
                     costPackaging += (it.spray_harga_beli * qty);
                  }
               }

               // Racikan
               (it.racikan || []).forEach(r => {
                  const hSatuan = (r.satuan || 'kg').toLowerCase();
                  const hBeliSat = r.harga_per_satuan || r.hpp_bahan || 0;
                  const hBeliPerKecil = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'g' || hSatuan === 'gram') ? (hBeliSat / 1000) : hBeliSat;
                  const volMl = ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * volVariant;
                  
                  if (volMl > 0 && hBeliPerKecil > 0) {
                     const catAsli = r.kategori_asli || r.kategori || r.category || 'Bibit';
                     const itemName = r.bahan_nama || r.nama_bahan || r.bahan || r.nama || r.name || r.item_name || r.komposisi || r.nama_barang || r.nama_item || r.ingredient || r.nama_produk || 'Bahan Cair';
                     const totalVol = volMl * qty;
                     const displayUnit = hSatuan === 'liter' || hSatuan === 'ml' ? 'ml' : 'gram';
                     addAggItem(itemName, 'Bahan Cair', catAsli, totalVol, displayUnit, hBeliPerKecil);
                     costBahanCair += (hBeliPerKecil * totalVol);
                  }
               });
            });
         } else {
            let variantName = p.nama_variant || p.variant || p.nama || p.name || po.varianLabel || po.varian || 'Produk Utama';
            variantName = variantName.replace(/\s*\(?\s*\d+\s*pcs\s*\)?\s*/gi, '').trim();
            const fallbackMl = parseFloat(p.nama_variant?.match(/\((\d+)ml\)/)?.[1] || 0);
            const volVariant = p.custom_ukuran_aktual_ml || p.botol_ukuran_aktual || p.botol_ukuran_label || p.botol_ukuran_client || p.ukuran_botol_ml || p.volume_ml || fallbackMl;
            if (volVariant > 0 && !variantName.toLowerCase().includes('ml')) variantName += ` (${volVariant} ml)`;

            const vReject = (po._rejectsByVariant || {})[variantName] || 0;
            const originalQty = p.qty_variant || p.qty || 1;
            const qty = Math.max(0, originalQty - vReject);

            if (qty <= 0) return; // Skip if all rejected
            
            let hasComponents = false;

            // Packaging
            if (p.is_fullset || p.hpp_kemasan !== undefined) {
               const pPrice = p.fullset_harga_beli || p.hpp_kemasan || 0;
               if (pPrice > 0) {
                  addAggItem(p.fullset_nama || p.nama_kemasan || 'Kemasan Fullset', 'Packaging', 'Fullset', qty, 'pcs', pPrice);
                  costPackaging += (pPrice * qty);
                  hasComponents = true;
               }
            } else {
               if (p.botol_harga_beli > 0) {
                  addAggItem(p.botol_nama || p.nama_botol || 'Botol', 'Packaging', 'Botol', qty, 'pcs', p.botol_harga_beli);
                  costPackaging += (p.botol_harga_beli * qty);
                  hasComponents = true;
               }
               if (p.tutup_harga_beli > 0) {
                  addAggItem(p.tutup_nama || p.nama_tutup || 'Tutup', 'Packaging', 'Tutup', qty, 'pcs', p.tutup_harga_beli);
                  costPackaging += (p.tutup_harga_beli * qty);
                  hasComponents = true;
               }
               if (p.spray_harga_beli > 0) {
                  addAggItem(p.spray_nama || p.nama_spray || 'Spray', 'Packaging', 'Spray', qty, 'pcs', p.spray_harga_beli);
                  costPackaging += (p.spray_harga_beli * qty);
                  hasComponents = true;
               }
            }

            // Racikan
            (p.racikan || []).forEach(r => {
               const hSatuan = (r.satuan || 'kg').toLowerCase();
               const hBeliSat = r.harga_per_satuan || r.hpp_bahan || 0;
               const hBeliPerKecil = (hSatuan === 'kg' || hSatuan === 'liter' || hSatuan === 'g' || hSatuan === 'gram') ? (hBeliSat / 1000) : hBeliSat;
               const volMl = ((parseFloat(String(r.persentase).replace(',', '.')) || 0) / 100) * volVariant;
               
               if (volMl > 0 && hBeliPerKecil > 0) {
                  const catAsli = r.kategori_asli || r.kategori || r.category || 'Bibit';
                  const itemName = r.bahan_nama || r.nama_bahan || r.bahan || r.nama || r.name || r.item_name || r.komposisi || r.nama_barang || r.nama_item || r.ingredient || r.nama_produk || 'Bahan Cair';
                  const totalVol = volMl * qty;
                  const displayUnit = hSatuan === 'liter' || hSatuan === 'ml' ? 'ml' : 'gram';
                  addAggItem(itemName, 'Bahan Cair', catAsli, totalVol, displayUnit, hBeliPerKecil);
                  costBahanCair += (hBeliPerKecil * totalVol);
                  hasComponents = true;
               }
            });

            // Fallback for legacy items without components
            if (!hasComponents && p.price && p.category) {
               const itemCost = p.isClientProvided ? 0 : (qty * p.price);
               if (p.category === 'Packaging') costPackaging += itemCost;
               if (p.category === 'Bahan Cair') costBahanCair += itemCost;
               if (p.category === 'Item Tambahan') costItemTambahan += itemCost;
               
               addAggItem(variantName ? `${p.category} ${variantName}` : p.name, p.category, p.category, qty, 'pcs', p.price);
            }
         }
      });
      
      const actualItems = Object.values(aggItems);
      po._actualItems = actualItems;
      
      const poTotalBiaya = costPackaging + costBahanCair + costItemTambahan;
      totalBiayaProduksi += poTotalBiaya;

      return {
        ...po,
        totalBiaya: poTotalBiaya,
        hpp: Math.round(poTotalBiaya / (po._computedQty || 1)),
        costs: {
          packaging: costPackaging,
          bahanCair: costBahanCair,
          itemTambahan: costItemTambahan
        },
        percentages: {
          packaging: poTotalBiaya > 0 ? (costPackaging / poTotalBiaya) * 100 : 0,
          bahanCair: poTotalBiaya > 0 ? (costBahanCair / poTotalBiaya) * 100 : 0,
          itemTambahan: poTotalBiaya > 0 ? (costItemTambahan / poTotalBiaya) * 100 : 0,
        }
      };
    });

    const averageHPP = processedPOs.length > 0 
      ? Math.round(totalBiayaProduksi / processedPOs.reduce((sum, po) => sum + po._computedQty, 0))
      : 0;

    return { totalBiayaProduksi, averageHPP, pos: processedPOs };
  }, [activePOs, simulatedPOIds]);

  const laporanData = useMemo(() => {
    let costPackaging = 0;
    let costBahanCair = 0;
    let costItemTambahan = 0;
    let allItems = [];

    dashboardData.pos.forEach(po => {
      (po._actualItems || po.items || []).forEach(item => {
        const itemCost = item.isClientProvided ? 0 : (item.qty * item.price);
        if (item.category === 'Packaging') costPackaging += itemCost;
        if (item.category === 'Bahan Cair') costBahanCair += itemCost;
        if (item.category === 'Item Tambahan') costItemTambahan += itemCost;
        
        allItems.push({
          ...item,
          totalBiaya: itemCost,
          poId: po.id,
          poBrand: po.brand,
          date: po.orderDate || '2026-07-15' // mock date for filtering
        });
      });
    });

    const totalBiaya = costPackaging + costBahanCair + costItemTambahan;
    
    let biggestCategory = 'Packaging';
    let biggestCategoryCost = costPackaging;
    if (costBahanCair > biggestCategoryCost) {
      biggestCategory = 'Bahan Cair';
      biggestCategoryCost = costBahanCair;
    }
    if (costItemTambahan > biggestCategoryCost) {
      biggestCategory = 'Item Tambahan';
      biggestCategoryCost = costItemTambahan;
    }

    return {
      costPackaging,
      costBahanCair,
      costItemTambahan,
      totalBiaya,
      allItems,
      biggestCategory,
      biggestCategoryCost
    };
  }, [dashboardData.pos]);

  // Filter
  const filteredPOs = dashboardData.pos.filter(po => {
    // 1. Search Query
    const brandStr = po._computedBrand || '';
    const idStr = po.id || '';
    const matchSearch = brandStr.toLowerCase().includes(searchQuery.toLowerCase()) || idStr.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchSearch) return false;

    // 2. PIC Filter
    if (poPicFilter !== 'Semua PIC' && po._computedPic !== poPicFilter) return false;

    // 3. Brand Filter
    if (poBrandFilter !== 'Semua Brand' && po._computedBrand !== poBrandFilter) return false;

    return true;
  });

  const totalCardPages = Math.ceil(filteredPOs.length / cardLimit);
  const paginatedCards = filteredPOs.slice((cardPage - 1) * cardLimit, cardPage * cardLimit);

  const selectedPO = useMemo(() => {
    return dashboardData.pos.find(po => po.id === selectedPOId);
  }, [dashboardData.pos, selectedPOId]);

  if (selectedPO) {
    const categories = ['Packaging', 'Bahan Cair', 'Item Tambahan'];
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4 mb-2">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => {
              setSelectedPOId(null);
              setDetailFilter('Semua');
            }}
            className="hover:bg-orange-50 text-[#8C6208] h-10 w-10 rounded-full shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-black text-[#302621] tracking-tight">{selectedPO._computedBrand || selectedPO.brand || selectedPO.clientName || 'Brand Tidak Diketahui'}</h1>
            <p className="text-sm text-[#7A695B] font-semibold">{selectedPO.id} &bull; PIC: {selectedPO._computedPic || 'Unknown'} &bull; {(selectedPO._computedQty || 0).toLocaleString('id-ID')} pcs</p>
            
            <div className="flex flex-wrap gap-3 mt-3">
              <div className="bg-[#F9F5EC] px-3 py-1.5 rounded-lg border border-[#E6D5B8]">
                <span className="text-[10px] font-bold text-[#A39171] uppercase tracking-wider block mb-0.5">Varian</span>
                <p className="text-xs font-black text-[#8C6208]">{(selectedPO._computedVariants || []).join(', ') || '-'}</p>
              </div>
              <div className="bg-[#F9F5EC] px-3 py-1.5 rounded-lg border border-[#E6D5B8]">
                <span className="text-[10px] font-bold text-[#A39171] uppercase tracking-wider block mb-0.5">Aroma (Bibit)</span>
                <p className="text-xs font-black text-[#8C6208]">
                  {(() => {
                    const bibit = (selectedPO._actualItems || []).filter(i => (i.subCategory || '').toUpperCase() === 'BIBIT').map(i => i.name);
                    return bibit.length > 0 ? bibit.join(', ') : '-';
                  })()}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full">
          {/* FULL WIDTH: TABLE */}
          <Card className="border-[#E6D5B8] shadow-sm">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-black text-[#302621] tracking-wider uppercase">Rincian Item Per Kategori</h2>
                <div className="flex gap-2">
                  <Button variant="outline" className="border-[#E6D5B8] text-[#8C6208] font-bold rounded-xl h-9">
                    <Download className="w-4 h-4 mr-2" /> Download Excel
                  </Button>
                  {selectedPO._sjLink && selectedPO._productionOrder && (
                    <Button variant="outline" className="border-[#E6D5B8] text-emerald-700 font-bold rounded-xl h-9" onClick={() => setSuratJalanToPrint(selectedPO._productionOrder)}>
                      <Truck className="w-4 h-4 mr-2" /> Lihat SJ
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                <button 
                  onClick={() => setDetailFilter('Semua')}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors cursor-pointer ${detailFilter === 'Semua' ? 'bg-[#8C6208] text-white border border-[#8C6208]' : 'border border-[#E6D5B8] text-[#7A695B] bg-white hover:bg-slate-50'}`}
                >
                  Semua ({(selectedPO._actualItems || selectedPO.items || []).length})
                </button>
                {categories.map(cat => {
                  const items = (selectedPO._actualItems || selectedPO.items || []).filter(i => i.category === cat);
                  if (items.length === 0) return null;
                  return (
                    <button 
                      key={cat} 
                      onClick={() => setDetailFilter(cat)}
                      className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors cursor-pointer ${detailFilter === cat ? 'bg-[#8C6208] text-white border border-[#8C6208]' : 'border border-[#E6D5B8] text-[#7A695B] bg-white hover:bg-slate-50'}`}
                    >
                      {cat} ({items.length})
                    </button>
                  );
                })}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="text-[#A39171] uppercase tracking-wider font-bold border-b border-[#E6D5B8]/50">
                      <th className="pb-3 px-2 min-w-[200px]">Nama Item</th>
                      <th className="pb-3 text-center">Qty</th>
                      <th className="pb-3 text-center">Satuan</th>
                      <th className="pb-3 text-right">Harga Satuan</th>
                      <th className="pb-3 text-right">Total Biaya</th>
                      <th className="pb-3 text-right">% dari Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50/50">
                    {categories.map((cat, catIdx) => {
                      if (detailFilter !== 'Semua' && detailFilter !== cat) return null;

                      const catItems = (selectedPO._actualItems || selectedPO.items || []).filter(i => i.category === cat);
                      if (catItems.length === 0) return null;
                      
                      let catTotal = 0;
                      if (cat === 'Packaging') catTotal = selectedPO.costs.packaging;
                      if (cat === 'Bahan Cair') catTotal = selectedPO.costs.bahanCair;
                      if (cat === 'Item Tambahan') catTotal = selectedPO.costs.itemTambahan;
                      const catPct = selectedPO.totalBiaya > 0 ? (catTotal / selectedPO.totalBiaya) * 100 : 0;

                      let Icon = Package;
                      if (cat === 'Bahan Cair') Icon = Droplets;

                      const subCats = Array.from(new Set(catItems.map(i => i.subCategory || cat)));

                      return (
                        <React.Fragment key={catIdx}>
                          {/* Main Category Header */}
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <td colSpan={6} className="py-2.5 px-2 font-black text-[#8C6208] uppercase tracking-wide text-xs">
                              <div className="flex items-center gap-2"><Icon className="w-4 h-4" /> {cat}</div>
                            </td>
                          </tr>
                          
                          {subCats.map((subCat, subIdx) => {
                             const itemsInSubCat = catItems.filter(i => (i.subCategory || cat) === subCat);
                             return (
                               <React.Fragment key={subIdx}>
                                 {/* SubCategory Badge Row */}
                                 <tr>
                                   <td colSpan={6} className="pt-4 pb-2 px-2 border-b-0">
                                     <span className="inline-block px-2.5 py-1 bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-full text-[9px] font-black uppercase tracking-wider">
                                       {subCat}
                                     </span>
                                   </td>
                                 </tr>
                                 
                                 {itemsInSubCat.map((item, idx) => {
                                   const itemTotal = item.isClientProvided ? 0 : item.qty * item.price;
                                   const pct = selectedPO.totalBiaya > 0 ? (itemTotal / selectedPO.totalBiaya) * 100 : 0;
                                   return (
                                     <tr key={idx} className="group hover:bg-slate-50/50 transition-colors border-b border-slate-50">
                                       <td className="py-3 px-2 pl-4">
                                         <p className="font-bold text-[#302621]">{item.name}</p>
                                         {item.isClientProvided && <span className="text-[9px] font-bold text-emerald-600 mt-0.5 inline-block">Dari Klien</span>}
                                       </td>
                                       <td className="py-3 px-1 text-center font-medium text-[#7A695B]">{item.qty.toLocaleString('id-ID')}</td>
                                       <td className="py-3 px-1 text-center font-medium text-[#7A695B]">{item.unit || 'pcs'}</td>
                                       <td className="py-3 px-1 text-right font-medium text-[#7A695B]">
                                         {item.isClientProvided ? '-' : formatCurrency(item.price)}
                                       </td>
                                       <td className="py-3 px-1 text-right font-bold text-[#302621]">
                                         {item.isClientProvided ? 'Rp 0' : formatCurrency(itemTotal)}
                                       </td>
                                       <td className="py-3 px-1 text-right font-medium text-[#7A695B]">
                                         {pct.toFixed(1)}%
                                       </td>
                                     </tr>
                                   );
                                 })}
                               </React.Fragment>
                             );
                          })}
                          
                          {/* Category Total Row */}
                          <tr className="bg-[#F9F5EC]/50 font-black text-[#8C6208] border-t border-[#E6D5B8]">
                            <td colSpan={4} className="py-3 px-2 text-right">Total {cat}</td>
                            <td className="py-3 px-1 text-right">{formatCurrency(catTotal)}</td>
                            <td className="py-3 px-1 text-right">{catPct.toFixed(1)}%</td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                    {/* Grand Total Row */}
                    <tr className="bg-[#FFFDF9] border-t-2 border-[#E6D5B8]">
                      <td colSpan={4} className="py-4 px-2 font-black text-sm text-[#302621] uppercase tracking-wider">Total Keseluruhan</td>
                      <td className="py-4 px-1 text-right font-black text-sm text-[#302621]">{formatCurrency(selectedPO.totalBiaya)}</td>
                      <td className="py-4 px-1 text-right font-black text-sm text-[#302621]">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {selectedPO._totalRejects > 0 && (
                <div className="mt-6 border border-rose-100 rounded-xl overflow-hidden shadow-sm">
                  <div className="bg-rose-50 px-4 py-3 border-b border-rose-100 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-600" />
                    <h3 className="font-bold text-rose-900 text-sm">Rincian Reject Produksi</h3>
                  </div>
                  <div className="bg-white p-4">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-rose-50 text-rose-800 text-left">
                          <th className="pb-2 font-bold">Nama Varian</th>
                          <th className="pb-2 font-bold text-right">Jumlah Reject</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(selectedPO._rejectsByVariant || {}).map(([vName, rQty]) => (
                          <tr key={vName} className="border-b border-rose-50/50 last:border-0">
                            <td className="py-2 text-rose-900 font-medium">{vName}</td>
                            <td className="py-2 text-right text-rose-700 font-bold">{rQty} pcs</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

            </CardContent>
          </Card>
        </div>

        {suratJalanToPrint && (
          <SuratJalanPrint
            order={suratJalanToPrint}
            shipment={null}
            companySettings={companySettings}
            onClose={() => setSuratJalanToPrint(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-black text-[#302621] tracking-tight flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-[#8C6208]" /> Simulasi Biaya Produksi
          </h1>
          <p className="text-sm text-[#7A695B] mt-1">Pantau biaya produksi, HPP, dan efisiensi setiap Purchase Order.</p>
        </div>
        <div className="flex items-center gap-3">
          <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#8C6208] hover:bg-[#6e4c05] text-white font-bold h-10 px-5 rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Tambahkan Biaya Produksi
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[450px]">
              <DialogHeader>
                <DialogTitle className="text-[#302621]">Pilih Purchase Order</DialogTitle>
                <DialogDescription className="text-[#7A695B]">
                  Pilih PO dengan status belum berjalan untuk memulai pencatatan biaya produksi.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="flex flex-col gap-3">
                  <label className="text-sm font-semibold text-[#302621]">Pilih PIC</label>
                  <Select value={modalPic} onValueChange={(val) => { setModalPic(val); setModalBrand(''); setModalPOId(''); setModalPOSearch(''); }}>
                    <SelectTrigger className="w-full border-[#E6D5B8] focus:ring-[#8C6208] rounded-xl h-10">
                      <SelectValue placeholder="Pilih PIC..." />
                    </SelectTrigger>
                    <SelectContent>
                      {modalUniquePICs.map(pic => (
                        <SelectItem key={pic} value={pic}>{pic}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {modalPic && (
                  <div className="flex flex-col gap-3">
                    <label className="text-sm font-semibold text-[#302621]">Pilih Brand</label>
                    <Select value={modalBrand} onValueChange={(val) => { setModalBrand(val); setModalPOId(''); setModalPOSearch(''); }}>
                      <SelectTrigger className="w-full border-[#E6D5B8] focus:ring-[#8C6208] rounded-xl h-10">
                        <SelectValue placeholder="Pilih Brand..." />
                      </SelectTrigger>
                      <SelectContent>
                        {modalUniqueBrands.map(brand => (
                          <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {modalPic && modalBrand && (
                  <div className="flex flex-col gap-3">
                    <label className="text-sm font-semibold text-[#302621]">Pilih Invoice / PO</label>
                    <Input 
                      placeholder="Cari ID PO, varian, atau nama..." 
                      className="border-[#E6D5B8] focus-visible:ring-[#8C6208] rounded-xl h-10"
                      value={modalPOSearch}
                      onChange={(e) => setModalPOSearch(e.target.value)}
                    />
                    <div className="max-h-[150px] overflow-y-auto border border-[#E6D5B8] rounded-xl p-1 bg-white">
                      {modalAvailablePOs.length === 0 ? (
                        <div className="p-3 text-center text-sm text-[#7A695B]">Tidak ada PO ditemukan</div>
                      ) : (
                        modalAvailablePOs.map(po => (
                          <div 
                            key={po.id} 
                            onClick={() => setModalPOId(po.id)}
                            className={`p-2 px-3 text-sm rounded-lg cursor-pointer flex justify-between items-center transition-colors ${modalPOId === po.id ? 'bg-[#F9F5EC] border border-[#8C6208] text-[#302621] font-semibold' : 'hover:bg-slate-50 text-[#7A695B] border border-transparent'}`}
                          >
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-[#302621]">{po.id}</span>
                                {po.date && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{po.date}</span>}
                              </div>
                              <span className="text-xs">({po._computedBrand} - {po.varianLabel || po.varian || 'Tanpa Varian'})</span>
                            </div>
                            {modalPOId === po.id && <Check className="w-4 h-4 text-[#8C6208]" />}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  disabled={!modalPOId}
                  className="bg-[#8C6208] hover:bg-[#6e4c05] text-white rounded-xl w-full sm:w-auto px-6 disabled:opacity-50"
                  onClick={() => {
                    if (!simulatedPOIds.includes(modalPOId)) {
                      setSimulatedPOIds(prev => [...prev, modalPOId]);
                    }
                    setSelectedPOId(modalPOId);
                    setActiveMainTab('cards');
                    setIsModalOpen(false);
                  }}
                >
                  Lanjutkan
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={handlePrint} variant="outline" className="border-[#E6D5B8] text-[#302621] font-bold h-10 px-4 rounded-xl bg-white hover:bg-slate-50">
            <Download className="w-4 h-4 mr-2 text-[#7A695B]" />
            Export
          </Button>
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="flex items-center gap-1 bg-white border border-[#E6D5B8] p-1.5 rounded-full w-full sm:max-w-[600px]">
        <button 
          onClick={() => setActiveMainTab('cards')}
          className={`flex-1 flex justify-center items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-colors ${activeMainTab === 'cards' ? 'bg-[#F9F5EC] text-[#8C6208]' : 'text-[#7A695B] hover:bg-slate-50'}`}
        >
          <Activity className="w-4 h-4" /> Pencatatan PO (Cards)
        </button>
        <button 
          onClick={() => setActiveMainTab('laporan')}
          className={`flex-1 flex justify-center items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-colors ${activeMainTab === 'laporan' ? 'bg-[#F9F5EC] text-[#8C6208]' : 'text-[#7A695B] hover:bg-slate-50'}`}
        >
          <FileText className="w-4 h-4" /> Laporan Kategori
        </button>
      </div>

      {/* ── CONDITIONAL RENDER BASED ON TAB ── */}
      {activeMainTab === 'cards' ? (
        <>
          {/* ── TOP STATS ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Total PO */}
        <Card className="border-[#E6D5B8] shadow-sm overflow-hidden md:col-span-1">
          <CardContent className="p-5 relative pb-0">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center shrink-0 border border-orange-100">
                <FileText className="w-6 h-6 text-[#8C6208]" />
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-bold text-[#302621] uppercase tracking-wider mb-1">Total Invoice</p>
                <h3 className="text-2xl font-black text-[#302621] mb-1">{dashboardData.pos.length}</h3>
                <p className="text-[10px] text-[#7A695B]">Dalam Simulasi</p>
              </div>
            </div>
            <Sparkline color="#E6D5B8" />
          </CardContent>
        </Card>

        {/* Total Biaya Produksi */}
        <Card className="border-[#E6D5B8] shadow-sm overflow-hidden md:col-span-2 xl:col-span-3">
          <CardContent className="p-5 relative pb-0">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center shrink-0 border border-orange-100">
                <Coins className="w-6 h-6 text-[#8C6208]" />
              </div>
              <div className="flex-1">
                <p className="text-[11px] font-bold text-[#302621] uppercase tracking-wider mb-1">Total Biaya Produksi</p>
                <h3 className="text-2xl font-black text-[#302621] mb-1">{formatCurrency(dashboardData.totalBiayaProduksi)}</h3>
                <p className="text-[10px] text-[#7A695B]">Total seluruh biaya</p>
              </div>
            </div>
            <Sparkline color="#8C6208" />
          </CardContent>
        </Card>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input 
            placeholder="Cari Invoice, produk, atau kode..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-white border-[#E6D5B8] focus-visible:ring-[#8C6208] h-10 rounded-xl w-full"
          />
        </div>
        <div className="flex gap-2 shrink-0 overflow-x-auto pb-1">
          <Select value={poPicFilter} onValueChange={setPoPicFilter}>
            <SelectTrigger className={`w-[140px] bg-white h-10 rounded-xl text-xs font-semibold focus:ring-1 focus:ring-[#8C6208] transition-colors ${poPicFilter !== 'Semua PIC' ? 'border-[#8C6208] text-[#8C6208] bg-[#F9F5EC]' : 'border-[#E6D5B8]'}`}>
              <SelectValue placeholder="Semua PIC" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Semua PIC">Semua PIC</SelectItem>
              {uniquePICs.map(pic => (
                <SelectItem key={pic} value={pic}>{pic}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={poBrandFilter} onValueChange={setPoBrandFilter}>
            <SelectTrigger className={`w-[140px] bg-white h-10 rounded-xl text-xs font-semibold focus:ring-1 focus:ring-[#8C6208] transition-colors ${poBrandFilter !== 'Semua Brand' ? 'border-[#8C6208] text-[#8C6208] bg-[#F9F5EC]' : 'border-[#E6D5B8]'}`}>
              <SelectValue placeholder="Semua Brand" />
            </SelectTrigger>
            <SelectContent>
              <div className="px-2 py-2 border-b border-slate-100">
                <div className="relative">
                  <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                  <input 
                    type="text" 
                    placeholder="Cari brand..."
                    value={poBrandSearch}
                    onChange={(e) => setPoBrandSearch(e.target.value)}
                    className="w-full text-xs h-7 pl-7 pr-2 rounded-md border border-slate-200 focus:outline-none focus:border-[#8C6208]"
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
              <SelectItem value="Semua Brand">Semua Brand</SelectItem>
              {uniqueBrands.filter(b => b.toLowerCase().includes(poBrandSearch.toLowerCase())).map(b => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={poTimeFilter} onValueChange={setPoTimeFilter}>
            <SelectTrigger className={`w-[160px] h-10 rounded-xl text-xs font-semibold focus:ring-1 focus:ring-[#8C6208] transition-colors ${poTimeFilter !== 'Semua Waktu' ? 'border-[#8C6208] text-[#8C6208] bg-[#F9F5EC]' : 'bg-white border-[#E6D5B8]'}`}>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <SelectValue placeholder="Semua Waktu" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Semua Waktu">Semua Waktu</SelectItem>
              <SelectItem value="1 Hari Terakhir">1 Hari Terakhir</SelectItem>
              <SelectItem value="7 Hari Terakhir">7 Hari Terakhir</SelectItem>
              <SelectItem value="1 Bulan Terakhir">1 Bulan Terakhir</SelectItem>
              <SelectItem value="Custom Date">Custom Date</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {poTimeFilter === 'Custom Date' && (
        <div className="p-4 bg-white border border-slate-200 rounded-xl flex flex-col sm:flex-row gap-4 items-center animate-in fade-in slide-in-from-top-2 duration-300 w-fit">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] font-bold text-[#1E3A8A] uppercase tracking-wider mb-2 block">Mulai Tanggal</label>
            <div className="relative">
              <Calendar className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input 
                type="date" 
                value={poCustomStart}
                onChange={(e) => setPoCustomStart(e.target.value)}
                className="w-full h-10 pl-9 pr-3 text-sm rounded-lg border border-slate-200 focus:border-[#8C6208] focus:ring-1 focus:ring-[#8C6208] focus:outline-none bg-white text-slate-600 transition-colors"
                title="Pilih tanggal mulai"
              />
            </div>
          </div>
          <div className="text-slate-300 px-2 mt-5 hidden sm:block">-</div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] font-bold text-[#1E3A8A] uppercase tracking-wider mb-2 block">Sampai Tanggal</label>
            <div className="relative">
              <Calendar className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input 
                type="date" 
                value={poCustomEnd}
                onChange={(e) => setPoCustomEnd(e.target.value)}
                className="w-full h-10 pl-9 pr-3 text-sm rounded-lg border border-slate-200 focus:border-[#8C6208] focus:ring-1 focus:ring-[#8C6208] focus:outline-none bg-white text-slate-600 transition-colors"
                title="Pilih tanggal sampai"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── CARDS GRID ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {paginatedCards.map((po) => (
          <Card key={po.id} className="border-[#E6D5B8] shadow-sm flex flex-col bg-white hover:border-[#8C6208] transition-colors rounded-2xl overflow-hidden">
            <CardContent className="p-0 flex-1 flex flex-col">
              
              {/* Card Top */}
              <div className="p-5 border-b border-slate-100 flex justify-between items-start">
                <div>
                  <h3 className="text-[15px] font-black text-[#302621] mb-1 leading-tight">{po._computedBrand}</h3>
                  <p className="text-[11px] text-[#7A695B] line-clamp-2 max-w-[280px] pr-4 mb-2 leading-relaxed" title={po._computedVariants?.join(', ') || ''}>
                    {po._computedVariants 
                      ? po._computedVariants.map((v, i) => (
                          <span key={i} className="whitespace-nowrap inline-block">{v}{i < po._computedVariants.length - 1 ? ', ' : ''}</span>
                        )) 
                      : <span className="whitespace-nowrap inline-block">Tanpa Varian</span>
                    }
                  </p>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 rounded border-[#E6D5B8] text-[#7A695B] bg-white">PIC: {po._computedPic || po.pic || 'Tidak Ada'}</Badge>
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 rounded border-emerald-200 text-emerald-700 bg-emerald-50">
                      {po._computedVariants ? po._computedVariants.length : 1} Varian
                    </Badge>
                    {po._totalRejects > 0 && (
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0 rounded border-rose-200 text-rose-700 bg-rose-50">
                        Reject: {po._totalRejects} pcs
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <p className="text-[10px] font-bold text-[#8C6208] bg-orange-50 px-2 py-0.5 rounded uppercase tracking-wide" title="Kode Invoice">
                    {po.id}
                  </p>
                  <div className="text-right mt-1">
                    <p className="text-[9px] font-bold text-[#7A695B] uppercase mb-0.5">Jumlah</p>
                    <p className="text-xs font-black text-[#302621]">{(po._computedQty || 0).toLocaleString('id-ID')} <span className="text-[9px] font-medium text-slate-400">pcs</span></p>
                  </div>
                </div>
              </div>

              {/* Card Middle (Stats) */}
              <div className="grid grid-cols-3 divide-x divide-slate-100 border-b border-slate-100">
                <div className="p-3 text-center bg-orange-50/30 col-span-2">
                  <p className="text-[10px] text-[#7A695B] font-semibold mb-1">Total Biaya Produksi</p>
                  <p className="text-[11px] sm:text-xs font-black text-[#8C6208]">{formatCurrency(po.totalBiaya)}</p>
                </div>
                <div className="p-3 text-center col-span-1">
                  <p className="text-[10px] text-[#7A695B] font-semibold mb-1">Rata-rata HPP/pcs</p>
                  <p className="text-[11px] sm:text-xs font-black text-[#302621]">{formatCurrency(po.hpp)}</p>
                </div>
              </div>

              {/* Progress Bars */}
              <div className="p-5 space-y-3.5 flex-1">
                {/* Packaging */}
                <div className="flex items-center gap-3">
                  <div className="w-[85px] flex items-center gap-1.5 shrink-0">
                    <Package className="w-3.5 h-3.5 text-[#7A695B]" />
                    <span className="text-[10px] font-bold text-[#7A695B]">Packaging</span>
                  </div>
                  <Progress value={po.percentages.packaging} className="h-1.5 bg-slate-100 flex-1 [&>div]:bg-[#8C6208]" />
                  <div className="w-[30px] text-right shrink-0">
                    <span className="text-[9px] font-bold text-[#7A695B]">{po.percentages.packaging.toFixed(0)}%</span>
                  </div>
                  <div className="w-[110px] text-right shrink-0">
                    <span className="text-[10px] font-bold text-[#302621]">{formatCurrency(po.costs.packaging)}</span>
                  </div>
                </div>

                {/* Bahan Cair */}
                <div className="flex items-center gap-3">
                  <div className="w-[85px] flex items-center gap-1.5 shrink-0">
                    <Droplets className="w-3.5 h-3.5 text-[#7A695B]" />
                    <span className="text-[10px] font-bold text-[#7A695B]">Bahan Cair</span>
                  </div>
                  <Progress value={po.percentages.bahanCair} className="h-1.5 bg-slate-100 flex-1 [&>div]:bg-amber-500" />
                  <div className="w-[30px] text-right shrink-0">
                    <span className="text-[9px] font-bold text-[#7A695B]">{po.percentages.bahanCair.toFixed(0)}%</span>
                  </div>
                  <div className="w-[110px] text-right shrink-0">
                    <span className="text-[10px] font-bold text-[#302621]">{formatCurrency(po.costs.bahanCair)}</span>
                  </div>
                </div>

                {/* Item Tambahan */}
                {po.costs.itemTambahan > 0 && (
                  <div className="flex items-center gap-3">
                    <div className="w-[85px] flex items-center gap-1.5 shrink-0">
                      <Package className="w-3.5 h-3.5 text-[#7A695B]" />
                      <span className="text-[10px] font-bold text-[#7A695B]">Item Tamb</span>
                    </div>
                    <Progress value={po.percentages.itemTambahan} className="h-1.5 bg-slate-100 flex-1 [&>div]:bg-emerald-600" />
                    <div className="w-[30px] text-right shrink-0">
                      <span className="text-[9px] font-bold text-[#7A695B]">{po.percentages.itemTambahan.toFixed(0)}%</span>
                    </div>
                    <div className="w-[110px] text-right shrink-0">
                      <span className="text-[10px] font-bold text-[#302621]">{formatCurrency(po.costs.itemTambahan)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Card Footer */}
              <div className="p-3 border-t border-slate-100 flex flex-col gap-2 bg-slate-50/50">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-[#302621]">{(po._actualItems || po.items || []).length} Item</span>
                  <button 
                    onClick={() => setSelectedPOId(po.id)}
                    className="text-[10px] font-bold text-[#8C6208] hover:text-[#6e4c05] flex items-center gap-1"
                  >
                    Lihat Detail <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                <div className="flex items-center justify-between border-t border-slate-200/60 pt-2">
                  <span className="text-[9px] text-slate-400 font-medium flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Tgl Selesai: {po._computedDate}
                  </span>
                  <span className="text-[9px] text-slate-400 font-medium">Update: {getTimeAgo(po.rawRecord?.updated_at || po.updated_at || po.created_at || po.date)}</span>
                </div>
              </div>

            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── PAGINATION ── */}
      {totalCardPages > 0 && (
        <div className="flex items-center justify-between px-2 pt-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-[#7A695B]">Row per Page</span>
              <Select value={cardLimit.toString()} onValueChange={(val) => { setCardLimit(Number(val)); setCardPage(1); }}>
                <SelectTrigger className="w-[70px] h-8 bg-white border-[#E6D5B8] rounded-lg text-xs font-semibold focus:ring-1 focus:ring-[#8C6208]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6</SelectItem>
                  <SelectItem value="12">12</SelectItem>
                  <SelectItem value="24">24</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-[11px] text-[#7A695B] font-medium">
              Menampilkan <span className="font-bold text-[#302621]">{paginatedCards.length}</span> dari <span className="font-bold text-[#302621]">{filteredPOs.length}</span> Simulasi
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button 
              onClick={() => setCardPage(p => Math.max(1, p - 1))}
              disabled={cardPage === 1}
              className="p-2 rounded-full hover:bg-[#F9F5EC] text-[#7A695B] disabled:opacity-30 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-1 items-center">
              {getPaginationItems(cardPage, totalCardPages).map((item, idx) => {
                if (item === '...') {
                  return (
                    <span key={`dots-${idx}`} className="w-8 h-8 flex items-center justify-center text-xs font-bold text-[#7A695B] select-none">
                      ...
                    </span>
                  );
                }
                return (
                  <button
                    key={item}
                    onClick={() => setCardPage(item)}
                    className={`w-8 h-8 rounded-full text-xs font-bold transition-colors flex items-center justify-center ${
                      cardPage === item 
                        ? 'bg-[#8C6208] text-white shadow-sm' 
                        : 'text-[#7A695B] hover:bg-[#F9F5EC]'
                    }`}
                  >
                    {item}
                  </button>
                );
              })}
            </div>
            <button 
              onClick={() => setCardPage(p => Math.min(totalCardPages, p + 1))}
              disabled={cardPage === totalCardPages}
              className="p-2 rounded-full hover:bg-[#F9F5EC] text-[#7A695B] disabled:opacity-30 transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
        </>
      ) : (
        /* ── LAPORAN KATEGORI VIEW ── */
        <div className="space-y-6">
          {/* Top Stats for Laporan */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-[#E6D5B8] shadow-sm md:col-span-1">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F9F5EC] flex items-center justify-center shrink-0 border border-[#E6D5B8]">
                  <FileText className="w-5 h-5 text-[#8C6208]" />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[#A39171] uppercase tracking-wider mb-0.5">Jumlah Transaksi (Item)</p>
                  <h3 className="text-xl font-black text-[#302621]">{laporanData.allItems.length} <span className="text-sm font-medium text-[#7A695B]">Record</span></h3>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[#E6D5B8] shadow-sm md:col-span-2">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F9F5EC] flex items-center justify-center shrink-0 border border-[#E6D5B8]">
                  <span className="text-[#8C6208] font-black text-lg">$</span>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-[#A39171] uppercase tracking-wider mb-0.5">Total Biaya Produksi</p>
                  <h3 className="text-2xl font-black text-[#302621]">{formatCurrency(laporanData.totalBiaya)}</h3>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Proporsi Biaya Bar Chart */}
          <Card className="border-[#E6D5B8] shadow-sm overflow-hidden">
            <div className="bg-[#FFFDF9] border-b border-[#E6D5B8]/50 px-6 py-4">
              <h3 className="font-black text-[#A39171] text-xs tracking-wider uppercase">Proporsi Biaya Produksi Per Kategori</h3>
            </div>
            <CardContent className="p-6 space-y-6">
              {[
                { label: 'Packaging', cost: laporanData.costPackaging, color: '#8C6208', icon: Package },
                { label: 'Bahan Cair', cost: laporanData.costBahanCair, color: '#F59E0B', icon: Droplets },
                { label: 'Item Tambahan', cost: laporanData.costItemTambahan, color: '#059669', icon: Package }
              ].map((cat, i) => {
                const pct = laporanData.totalBiaya > 0 ? (cat.cost / laporanData.totalBiaya) * 100 : 0;
                return (
                  <div key={i} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <cat.icon className="w-4 h-4 text-[#7A695B]" />
                        <span className="text-xs font-bold text-[#302621]">{cat.label}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-black text-[#302621]">{formatCurrency(cat.cost)} </span>
                        <span className="text-xs font-medium text-[#7A695B]">({pct.toFixed(1)}%)</span>
                      </div>
                    </div>
                    <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: cat.color }} />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Daftar Transaksi Produksi Filter Section */}
          <Card className="border-[#E6D5B8] shadow-sm mb-6">
            <CardContent className="p-6">
              <div className="mb-4">
                <h2 className="text-xl font-black text-[#302621] tracking-tight">Daftar Transaksi Produksi</h2>
                <p className="text-sm text-[#7A695B]">Kelola dan filter data pengeluaran bahan dan produksi.</p>
              </div>
              
              <div className="flex flex-col lg:flex-row gap-4 items-end border-t border-[#E6D5B8]/50 pt-4">
                <div className="flex-1 flex flex-col md:flex-row gap-4 w-full">
                  {/* Kategori Select */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-[#A39171] uppercase tracking-wider shrink-0">Kategori:</span>
                    <Select value={laporanCatSelect} onValueChange={setLaporanCatSelect}>
                      <SelectTrigger className="w-[180px] bg-white border-[#E6D5B8] h-10 rounded-xl text-sm font-semibold focus:ring-1 focus:ring-[#8C6208]">
                        <SelectValue placeholder="Semua Kategori" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Semua Kategori">Semua Kategori</SelectItem>
                        <SelectItem value="Packaging">Packaging</SelectItem>
                        <SelectItem value="Bahan Cair">Bahan Cair</SelectItem>
                        <SelectItem value="Item Tambahan">Item Tambahan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Search Input */}
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input 
                      placeholder="Cari deskripsi atau tipe..." 
                      value={laporanSearch}
                      onChange={(e) => setLaporanSearch(e.target.value)}
                      className="pl-9 bg-white border-[#E6D5B8] focus-visible:ring-[#8C6208] h-10 rounded-xl w-full"
                    />
                  </div>
                </div>

                {/* Dates */}
                <div className="flex items-center gap-4 w-full lg:w-auto">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-[#A39171] uppercase tracking-wider shrink-0">Dari:</span>
                    <Input 
                      type="date"
                      value={laporanDateFrom}
                      onChange={(e) => setLaporanDateFrom(e.target.value)}
                      className="bg-white border-[#E6D5B8] focus-visible:ring-[#8C6208] h-10 rounded-xl w-[140px] text-sm text-[#7A695B]"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-[#A39171] uppercase tracking-wider shrink-0">S/D:</span>
                    <Input 
                      type="date"
                      value={laporanDateTo}
                      onChange={(e) => setLaporanDateTo(e.target.value)}
                      className="bg-white border-[#E6D5B8] focus-visible:ring-[#8C6208] h-10 rounded-xl w-[140px] text-sm text-[#7A695B]"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Table Rincian Item Per Kategori */}
          <Card className="border-[#E6D5B8] shadow-sm">
            <CardContent className="p-6">


              <div className="overflow-x-auto border rounded-xl border-[#E6D5B8]/50">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50">
                    <tr className="text-[#A39171] uppercase tracking-wider font-bold border-b border-[#E6D5B8]/50">
                      <th className="p-4 w-[120px]">Tanggal</th>
                      <th className="p-4 w-[180px]">Kategori & Tipe</th>
                      <th className="p-4 min-w-[200px]">Nama Item & Qty</th>
                      <th className="p-4 w-[160px]">Sumber PO</th>
                      <th className="p-4 text-right w-[140px]">Harga Satuan</th>
                      <th className="p-4 text-right w-[180px]">Total Biaya</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50/50">
                    {(() => {
                      let filteredItems = laporanData.allItems;
                      
                      // 1. Dropdown Filter
                      if (laporanCatSelect !== 'Semua Kategori') {
                        filteredItems = filteredItems.filter(i => i.category === laporanCatSelect);
                      }
                      
                      // 2. Search Filter
                      if (laporanSearch.trim() !== '') {
                        const q = laporanSearch.toLowerCase();
                        filteredItems = filteredItems.filter(i => 
                          i.name.toLowerCase().includes(q) || 
                          i.poId.toLowerCase().includes(q) ||
                          i.poBrand.toLowerCase().includes(q)
                        );
                      }
                      
                      // 4. Date Filter (Mock based on injected date)
                      if (laporanDateFrom) {
                        filteredItems = filteredItems.filter(i => new Date(i.date) >= new Date(laporanDateFrom));
                      }
                      if (laporanDateTo) {
                        filteredItems = filteredItems.filter(i => new Date(i.date) <= new Date(laporanDateTo));
                      }
                      
                      const totalPages = Math.ceil(filteredItems.length / laporanItemsPerPage);
                      const currentItems = filteredItems.slice((laporanPage - 1) * laporanItemsPerPage, laporanPage * laporanItemsPerPage);

                      if (currentItems.length === 0) {
                        return (
                          <tr>
                            <td colSpan={6} className="p-8 text-center text-slate-400 font-medium">Tidak ada data item.</td>
                          </tr>
                        );
                      }

                      return (
                        <>
                          {currentItems.map((item, idx) => (
                            <tr key={idx} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100/50 last:border-0">
                              <td className="p-4 font-medium text-[#7A695B] text-xs">
                                {new Date(item.date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </td>
                              <td className="p-4 font-bold text-[#302621]">
                                <div className="flex items-center gap-2 mb-1">
                                  {item.category === 'Packaging' ? <Package className="w-3.5 h-3.5 text-[#8C6208]" /> : (item.category === 'Bahan Cair' ? <Droplets className="w-3.5 h-3.5 text-[#8C6208]" /> : <Package className="w-3.5 h-3.5 text-[#8C6208]" />)}
                                  {item.category}
                                </div>
                                <span className="text-[10px] text-[#7A695B] font-medium ml-5 capitalize">{item.subCategory || '-'}</span>
                              </td>
                              <td className="p-4">
                                <div className="font-bold text-[#302621]">
                                  {item.name}
                                  {item.isClientProvided && <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700">Dari Klien</span>}
                                </div>
                                <div className="text-[10px] font-medium text-[#7A695B] mt-1">
                                  {item.qty.toLocaleString('id-ID')} {item.unit || 'pcs'}
                                </div>
                              </td>
                              <td className="p-4 font-medium text-[#7A695B]">
                                {item.poId} <br/> <span className="text-[10px]">{item.poBrand}</span>
                              </td>
                              <td className="p-4 text-right font-medium text-[#7A695B]">
                                {formatCurrency(item.price)}
                              </td>
                              <td className="p-4 text-right font-bold text-[#302621]">
                                {item.isClientProvided ? 'Rp 0' : formatCurrency(item.totalBiaya)}
                              </td>
                            </tr>
                          ))}
                          {totalPages > 1 && (
                            <tr className="bg-slate-50/50 border-t border-[#E6D5B8]/50">
                              <td colSpan={6} className="p-4">
                                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-[#7A695B]">Row per Page</span>
                                      <Select value={laporanItemsPerPage.toString()} onValueChange={(val) => { setLaporanItemsPerPage(Number(val)); setLaporanPage(1); }}>
                                        <SelectTrigger className="w-[70px] h-8 bg-white border-[#E6D5B8] rounded-full text-xs font-semibold focus:ring-1 focus:ring-[#8C6208]">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="10">10</SelectItem>
                                          <SelectItem value="25">25</SelectItem>
                                          <SelectItem value="50">50</SelectItem>
                                          <SelectItem value="100">100</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <p className="text-[11px] text-[#7A695B] font-medium">
                                      Menampilkan <span className="font-bold text-[#302621]">{currentItems.length}</span> dari <span className="font-bold text-[#302621]">{filteredItems.length}</span> item
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1.5">
                                    <button 
                                      onClick={() => setLaporanPage(p => Math.max(1, p - 1))}
                                      disabled={laporanPage === 1}
                                      className="p-2 rounded-full hover:bg-[#F9F5EC] text-[#7A695B] disabled:opacity-30 transition-colors"
                                    >
                                      <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <div className="flex gap-1 items-center">
                                      {getPaginationItems(laporanPage, totalPages).map((item, idx) => {
                                        if (item === '...') {
                                          return (
                                            <span key={`dots-${idx}`} className="w-8 h-8 flex items-center justify-center text-xs font-bold text-[#7A695B] select-none">
                                              ...
                                            </span>
                                          );
                                        }
                                        return (
                                          <button
                                            key={item}
                                            onClick={() => setLaporanPage(item)}
                                            className={`w-8 h-8 rounded-full text-xs font-bold transition-colors flex items-center justify-center ${
                                              laporanPage === item 
                                                ? 'bg-[#8C6208] text-white shadow-sm' 
                                                : 'text-[#7A695B] hover:bg-[#F9F5EC]'
                                            }`}
                                          >
                                            {item}
                                          </button>
                                        );
                                      })}
                                    </div>
                                    <button 
                                      onClick={() => setLaporanPage(p => Math.min(totalPages, p + 1))}
                                      disabled={laporanPage === totalPages}
                                      className="p-2 rounded-full hover:bg-[#F9F5EC] text-[#7A695B] disabled:opacity-30 transition-colors"
                                    >
                                      <ChevronRight className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Hidden Print Component */}
      <div style={{ position: "absolute", top: "-10000px", left: "-10000px" }}>
        <SimulasiProduksiReport 
          ref={printRef} 
          data={dashboardData} 
          dateRange={poTimeFilter === 'Custom Date' ? (poCustomStart && poCustomEnd ? `${poCustomStart} s/d ${poCustomEnd}` : 'Custom Date') : (poTimeFilter !== 'Semua Waktu' ? poTimeFilter : null)} 
        />
      </div>

    </div>
  );
}
